import { hashCode } from "../../../lib/scoreboardUtils";

export function makeMockStats({ code, players = [], playedDuration = 0 }) {
  const base = hashCode(code);

  const perPlayer = players.map((p, idx) => {
    const seed = hashCode(`${code}:${p.pseudo}:${p.id ?? idx}`);

    // Indices par niveau (1/2/3) + answer (mock)
    const hintsL1 = seed % 3; // 0..2
    const hintsL2 = (seed >> 2) % 3; // 0..2
    const hintsL3 = (seed >> 4) % 2; // 0..1
    const hintsAnswer = (seed >> 6) % 2; // 0..1 (mock)

    const hintsTotal = hintsL1 + hintsL2 + hintsL3 + hintsAnswer;

    // Erreurs (mock)
    const wrongPwd = seed % 6; // 0..5

    // Objets inspectés (mock)
    const interactions = 10 + (seed % 25); // 10..34

    // Taquin (seulement joueur 4, optionnel)
    let taquinMoves = null;
    if (idx === 3) {
      const played = (seed % 5) !== 0; // ~20% "non joué"
      taquinMoves = played ? 18 + (seed % 45) : null; // 18..62 ou null
    }

    // Puzzle perdu (seulement joueur 3, optionnel)
    let puzzleLostSec = null;
    if (idx === 2) {
      const played = (seed % 6) !== 0; // ~16% "non joué"
      puzzleLostSec = played
        ? 30 + ((base % 240) + (playedDuration % 90)) // ~0:30 -> ~5:30
        : null;
    }

    return {
      id: p.id ?? idx,
      pseudo: p.pseudo ?? `Joueur ${idx + 1}`,
      avatar: p.avatar,
      avatarLabel: p.avatarLabel ?? "Avatar",
      hints: {
        l1: hintsL1,
        l2: hintsL2,
        l3: hintsL3,
        answer: hintsAnswer,
        total: hintsTotal,
      },
      wrongPasswords: wrongPwd,
      interactions,
      taquinMoves,
      puzzleLostSec,
      timeLostSec: 0,
    };
  });

  const totalHintsL1 = perPlayer.reduce((a, p) => a + (p.hints?.l1 || 0), 0);
  const totalHintsL2 = perPlayer.reduce((a, p) => a + (p.hints?.l2 || 0), 0);
  const totalHintsL3 = perPlayer.reduce((a, p) => a + (p.hints?.l3 || 0), 0);
  const totalHintsAnswer = perPlayer.reduce((a, p) => a + (p.hints?.answer || 0), 0);

  const totalHints = totalHintsL1 + totalHintsL2 + totalHintsL3 + totalHintsAnswer;

  const wrongPwdTotal = perPlayer.reduce((a, p) => a + (p.wrongPasswords || 0), 0);
  const interactionsTotal = perPlayer.reduce((a, p) => a + (p.interactions || 0), 0);

  const j3 = perPlayer[2] || null;
  const j4 = perPlayer[3] || null;

  const puzzleLostSec = j3?.puzzleLostSec ?? null;
  const taquinMoves = j4?.taquinMoves ?? null;

  // Points (mock – ne sert plus pour le score final)
  const timeScore = Math.max(0, 6000 - Math.floor((playedDuration || 0) * 6));
  const penaltyHints =
    totalHintsL1 * 40 + totalHintsL2 * 90 + totalHintsL3 * 160 + totalHintsAnswer * 220;
  const penaltyErrors = wrongPwdTotal * 120;
  const points = Math.max(0, Math.round(timeScore - penaltyHints - penaltyErrors));

  // MVP ranking (simple)
  const ranking = [...perPlayer].sort(
    (a, b) =>
      (a.hints?.total || 0) - (b.hints?.total || 0) ||
      (a.wrongPasswords || 0) - (b.wrongPasswords || 0) ||
      (b.interactions || 0) - (a.interactions || 0)
  );

  return {
    totals: {
      hints: {
        l1: totalHintsL1,
        l2: totalHintsL2,
        l3: totalHintsL3,
        answer: totalHintsAnswer,
        total: totalHints,
      },
      wrongPwdTotal,
      interactionsTotal,
      timeLostTotal: 0,
      points, // mock
    },
    puzzleLostSec,
    taquinMoves,
    perPlayer,
    ranking,
  };
}

export function applyRealMetricsToStats(stats, metrics) {
  if (!stats) return stats;

  const byPlayer = metrics?.byPlayer || {};

  const totalErrors =
    typeof metrics?.totals?.errors === "number" ? metrics.totals.errors : null;

  const totalTimeLost =
    typeof metrics?.totals?.time_lost === "number" ? metrics.totals.time_lost : null;

  const totalInteractions =
    typeof metrics?.totals?.interactions === "number"
      ? metrics.totals.interactions
      : null;

  const totalHintsL1 =
    typeof metrics?.totals?.hints_l1 === "number" ? metrics.totals.hints_l1 : null;
  const totalHintsL2 =
    typeof metrics?.totals?.hints_l2 === "number" ? metrics.totals.hints_l2 : null;
  const totalHintsL3 =
    typeof metrics?.totals?.hints_l3 === "number" ? metrics.totals.hints_l3 : null;
  const totalHintsAnswer =
    typeof metrics?.totals?.hints_answer === "number"
      ? metrics.totals.hints_answer
      : null;

  const perPlayer = stats.perPlayer.map((p) => {
    const pid = String(p.id);

    const errors = typeof byPlayer?.[pid]?.errors === "number" ? byPlayer[pid].errors : 0;

    const timeLost =
      typeof byPlayer?.[pid]?.time_lost === "number" ? byPlayer[pid].time_lost : 0;

    const taquinMoves =
      typeof byPlayer?.[pid]?.taquin_moves === "number" ? byPlayer[pid].taquin_moves : null;

    const interactions =
      typeof byPlayer?.[pid]?.interactions === "number" ? byPlayer[pid].interactions : 0;

    const h1 =
      typeof byPlayer?.[pid]?.hints_l1 === "number" ? byPlayer[pid].hints_l1 : 0;
    const h2 =
      typeof byPlayer?.[pid]?.hints_l2 === "number" ? byPlayer[pid].hints_l2 : 0;
    const h3 =
      typeof byPlayer?.[pid]?.hints_l3 === "number" ? byPlayer[pid].hints_l3 : 0;
    const ha =
      typeof byPlayer?.[pid]?.hints_answer === "number" ? byPlayer[pid].hints_answer : 0;

    return {
      ...p,
      wrongPasswords: errors,
      timeLostSec: timeLost,
      taquinMoves,
      interactions,
      hints: {
        l1: h1,
        l2: h2,
        l3: h3,
        answer: ha,
        total: h1 + h2 + h3 + ha,
      },
    };
  });

  const wrongPwdTotal =
    totalErrors != null ? totalErrors : perPlayer.reduce((a, p) => a + (p.wrongPasswords || 0), 0);

  const timeLostTotal =
    totalTimeLost != null ? totalTimeLost : perPlayer.reduce((a, p) => a + (p.timeLostSec || 0), 0);

  const interactionsTotal =
    totalInteractions != null
      ? totalInteractions
      : perPlayer.reduce((a, p) => a + (p.interactions || 0), 0);

  const hintsL1Team =
    totalHintsL1 != null ? totalHintsL1 : perPlayer.reduce((a, p) => a + (p.hints?.l1 || 0), 0);
  const hintsL2Team =
    totalHintsL2 != null ? totalHintsL2 : perPlayer.reduce((a, p) => a + (p.hints?.l2 || 0), 0);
  const hintsL3Team =
    totalHintsL3 != null ? totalHintsL3 : perPlayer.reduce((a, p) => a + (p.hints?.l3 || 0), 0);
  const hintsAnswerTeam =
    totalHintsAnswer != null
      ? totalHintsAnswer
      : perPlayer.reduce((a, p) => a + (p.hints?.answer || 0), 0);

  const hintsTotalTeam = hintsL1Team + hintsL2Team + hintsL3Team + hintsAnswerTeam;

  const ranking = [...perPlayer].sort(
    (a, b) =>
      (a.hints?.total || 0) - (b.hints?.total || 0) ||
      (a.wrongPasswords || 0) - (b.wrongPasswords || 0) ||
      (b.interactions || 0) - (a.interactions || 0)
  );

  //  Puzzle joueur 3 = timeLostSec du 3e joueur (si présent)
  const j3 = perPlayer[2] || null;
  const puzzleLostSec = j3 ? (j3.timeLostSec || 0) : null;

  // Taquin équipe = taquinMoves du 4e joueur (si présent)
  const j4 = perPlayer[3] || null;
  const taquinMovesTeam = j4?.taquinMoves ?? null;

  return {
    ...stats,
    perPlayer,
    ranking,
    puzzleLostSec,
    taquinMoves: taquinMovesTeam,
    totals: {
      ...stats.totals,
      hints: {
        l1: hintsL1Team,
        l2: hintsL2Team,
        l3: hintsL3Team,
        answer: hintsAnswerTeam,
        total: hintsTotalTeam,
      },
      wrongPwdTotal,
      timeLostTotal,
      interactionsTotal,
      // Les points officiels viennent de la DB, on ne recalcule pas ici
      points: stats.totals?.points ?? 0,
    },
  };
}

/**
 * Retourne les stats spécifiques Origami à passer à Scoreboard via la prop `gameStats`.
 * Pour un autre jeu, on crée un fichier équivalent qui retourne ses propres stats.
 */
export function getOrigamiGameStats(stats, breakdown, data) {
  const is6Players = (data?.players?.length || 0) === 6;
  const puzzleLostSec = stats?.puzzleLostSec ?? null;
  const taquinMoves = stats?.taquinMoves ?? null;

  return {
    specialStats: [
      {
        id: "puzzle",
        label: "Temps \"perdu\" Joueur 3",
        value: breakdown.bonusPuzzle,
        sub: puzzleLostSec == null ? "Non joué" : `${Math.floor(puzzleLostSec / 60)}m ${(puzzleLostSec % 60).toString().padStart(2, "0")}s`,
        color: "text-rose-200",
      },
      {
        id: "taquin",
        label: '"Taquin" Joueur 4',
        value: breakdown.bonusTaquin,
        sub: taquinMoves == null ? "Non joué" : `${taquinMoves} coups`,
        color: "text-indigo-200",
      },
      ...(is6Players ? [{
        id: "empathie",
        label: "Bonus empathie",
        value: Number(breakdown.bonusEmpathie ?? 0) || 0,
        sub: 'Puzzle "Empathie" validé',
        color: "text-emerald-200",
      }] : []),
    ],
  };
}